import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

interface UsePageParamOptions {
  /** Query param name. Defaults to `page`. */
  key?: string
  /** Replace the history entry instead of pushing a new one. Defaults to false. */
  replace?: boolean
}

/**
 * Keeps a pagination page number in the URL query string (?page=N) so it
 * survives reloads, back/forward navigation and can be shared as a link.
 *
 * Page 1 is the implicit default and is never written to the URL, which keeps
 * the canonical (unpaginated) URL clean for SEO and sharing.
 */
export function usePageParam({ key = 'page', replace = false }: UsePageParamOptions = {}) {
  const [searchParams, setSearchParams] = useSearchParams()

  const parsed = parseInt(searchParams.get(key) || '1', 10)
  const page = Number.isNaN(parsed) || parsed < 1 ? 1 : parsed

  const setPage = useCallback((pg: number) => {
    const target = pg <= 1 ? null : String(pg)
    // No-op when already there, so repeated resets (e.g. a `setPage(1)` on
    // every keystroke of a search box) don't pile up history entries.
    if (searchParams.get(key) === target) return

    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (target === null) next.delete(key)
      else next.set(key, target)
      return next
    }, { replace })
  }, [searchParams, setSearchParams, key, replace])

  /** Sets the page and scrolls back to the top — the usual pager click handler. */
  const goToPage = useCallback((pg: number) => {
    setPage(pg)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [setPage])

  return { page, setPage, goToPage }
}

export default usePageParam
